
import React, { useState, useEffect } from 'react';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { userQueries } from '@/components/admin/dashboard/queries/user-queries';
import { TrendingUp, TrendingDown, RefreshCw, AlertTriangle, DollarSign } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ResizableSwappableCard } from '@/components/admin/dashboard/ResizableSwappableCard';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { useCurrencies } from '@/hooks/useCurrencies';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { FeatureGuard } from '@/components/common/FeatureGuard';

// Define the DashboardWidgetProps interface locally instead of importing it
interface DashboardWidgetProps {
  rearrangingEnabled: boolean;
  onRemove?: (id: string) => void;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  onResize?: (id: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => void;
  selectedYear?: number;
}

export function FinancialResultWidget({ 
  rearrangingEnabled, 
  onRemove, 
  size = 'medium', 
  onResize,
  selectedYear
}: DashboardWidgetProps) {
  const { t } = useTranslation();
  const [year, setYear] = useState(selectedYear || new Date().getFullYear());
  const { canUseCurrencyConversion } = useFeatureAccess();
  
  // Update year when selectedYear prop changes
  useEffect(() => {
    if (selectedYear) {
      setYear(selectedYear);
    }
  }, [selectedYear]);
  
  const { useUserPreferencesQuery } = useUserPreferences();
  const { useCurrencyQuery } = useCurrencies();
  const { data: userPreferences } = useUserPreferencesQuery();
  
  const { 
    data: defaultCurrencyData,
    isLoading: isDefaultCurrencyLoading
  } = useCurrencyQuery(userPreferences?.defaultCurrencyId || '');

  const { data, isLoading, error } = useQuery({
    queryKey: ['financial-result', year],
    queryFn: () => userQueries.getUserFinancialResult(year),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get data with fallbacks for each property
  const resultData = data?.data || {
    result: 0,
    totalCost: 0,
    totalIncome: 0,
    convertedResult: 0,
    convertedTotalCost: 0,
    convertedTotalIncome: 0,
    valuesByCurrency: [],
    conversionRates: {},
    defaultCurrencyId: null,
    defaultCurrency: null
  };
  
  const { 
    result, 
    totalCost, 
    totalIncome, 
    convertedResult, 
    convertedTotalCost, 
    convertedTotalIncome,
    defaultCurrency, 
    valuesByCurrency 
  } = resultData;
  
  // If user has a default currency AND currency conversion is enabled, show converted values
  const shouldShowConverted = !!userPreferences?.defaultCurrencyId && canUseCurrencyConversion;
  const finalResult = shouldShowConverted ? convertedResult : result;
  const finalTotalCost = shouldShowConverted ? convertedTotalCost : totalCost;
  const finalTotalIncome = shouldShowConverted ? convertedTotalIncome : totalIncome;
  
  const isPositive = finalResult >= 0;
  const currencySymbol = shouldShowConverted 
    ? (defaultCurrencyData?.symbol || '$') 
    : (defaultCurrency?.symbol || '$');

  if (error) {
    console.error('Error fetching financial result:', error);
  }

  // For debugging size changes
  useEffect(() => {
    console.log('FinancialResultWidget size changed to:', size);
  }, [size]);

  return (
    <ResizableSwappableCard
      id="financial-result"
      size={size}
      showHandle={rearrangingEnabled}
      showResizeControl={rearrangingEnabled}
      onRemove={rearrangingEnabled && onRemove ? () => onRemove("financial-result") : undefined}
      onResize={rearrangingEnabled && onResize ? (newSize) => {
        console.log('Resizing financial-result to:', newSize);
        onResize("financial-result", newSize);
      } : undefined}
      className="h-full"
    >
      <CardHeader className="pb-2 space-y-0">
        <CardTitle className="text-sm font-medium">Financial Result {year}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center">
              <div className={`p-2 rounded-full ${isPositive ? 'bg-green-100 dark:bg-green-900/20' : 'bg-red-100 dark:bg-red-900/20'} mr-4`}>
                {isPositive ? (
                  <TrendingUp className={`h-6 w-6 text-green-600 dark:text-green-400`} />
                ) : (
                  <TrendingDown className={`h-6 w-6 text-red-600 dark:text-red-400`} />
                )}
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Result</div>
                <div className={`text-2xl font-bold ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {currencySymbol} {Math.abs(finalResult).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                </div>
                
                {shouldShowConverted && valuesByCurrency.length > 1 && (
                  <FeatureGuard feature="currency_conversion">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1 text-xs mt-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900 rounded-full cursor-help max-w-fit">
                            <RefreshCw className="h-3 w-3 text-blue-500" />
                            <span className="text-blue-700 dark:text-blue-300">
                              {t('multiCurrency', 'Multi-currency conversion')}
                            </span>
                            <AlertTriangle className="h-3 w-3 text-amber-500 ml-0.5 opacity-70" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="bg-black text-white border-gray-800 p-3 max-w-[300px]">
                          <div className="space-y-2">
                            <p>{t('convertedFrom', 'Converted from multiple currencies')}:</p>
                            <ul className="space-y-1 text-xs">
                              {valuesByCurrency.map((currency) => (
                                <li key={currency.currencyId} className="flex justify-between">
                                  <span>{currency.code}:</span>
                                  <span>{currency.symbol} {currency.total.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                                </li>
                              ))}
                            </ul>
                            <p className="text-amber-300 text-xs flex items-start gap-1.5">
                              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span>{t('currencyDisclaimer', 'Currency conversions are estimates and may not reflect exact rates at the time of contract payment.')}</span>
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </FeatureGuard>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-2 pt-4 border-t">
              <div>
                <div className="text-xs text-muted-foreground">Total Income</div>
                <div className="text-lg font-medium text-green-600 dark:text-green-400">
                  {currencySymbol} {finalTotalIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total Cost</div>
                <div className="text-lg font-medium text-red-600 dark:text-red-400">
                  {currencySymbol} {finalTotalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })},-
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </ResizableSwappableCard>
  );
}
